/* global Blockly */

// Fields on a block sitting in a flyout (toolbox category, trashcan preview,
// etc.) are not supposed to be interactive until the block has actually been
// dragged out onto the canvas - only clicking/dragging the block itself
// (to create it on the workspace) should do anything, and the cursor should
// always read as "drag this block" (grab), never as "click/edit this part".
//
// Stock Blockly decides what a mouseup on a field should do via
// Blockly.Gesture.prototype.handleUp, which checks (in order) bubble click,
// field click, block click, then workspace click - only one branch runs:
//
//   isFieldClick_() ? doFieldClick_(e)
//     : isBlockClick_() ? doBlockClick_()
//     : isWorkspaceClick_() && doWorkspaceClick_()
//
// isFieldClick_() is true whenever the field is clickable AND
// (!flyout_ || !flyout_.autoClose). MADP-88814 deliberately sets the toolbox
// flyout's autoClose to false (see Toolbox.js#_keepFlyoutOpenOnBlockCreate)
// so a block can be dragged out without the flyout auto-closing - but that
// flag flip has a side effect: it also makes isFieldClick_() start returning
// true for every field on every block inside that flyout, so clicking a
// dropdown/button/checkbox/etc. on a flyout block now opens its editor (or
// runs its click handler) instead of being ignored, which was never the
// intent.
//
// An earlier version of this patch neutralized this by making
// Field.prototype.isClickable() return false for flyout fields instead.
// That's the wrong place to intervene: making isFieldClick_() itself return
// false doesn't just skip doFieldClick_(), it changes which branch of the
// handleUp ternary above runs at all - the click then falls through to
// isBlockClick_()/doBlockClick_(), an entirely different (and, for a plain
// click with no drag, essentially untested) code path that fires an async
// Blockly.Events.Ui "click" event and had the observable side effect of the
// flyout closing on click, which is exactly what MADP-88814 fixed and must
// not regress.
//
// Patch Gesture.prototype.doFieldClick_ instead - the single, subclass-agnostic
// choke point every field type (dropdown, button, checkbox, colour, date,
// image, text input, etc.) funnels through right before it would call the
// field's own showEditor_. This leaves Blockly's own click/drag
// classification (isFieldClick_/isBlockClick_/isWorkspaceClick_) completely
// untouched - the exact same branch runs as before - we just make that
// branch a no-op when the field's block is still sitting in a flyout.
const fieldFlyoutInteraction = function () {
    const originalDoFieldClick = Blockly.Gesture.prototype.doFieldClick_;
    Blockly.Gesture.prototype.doFieldClick_ = function (e) {
        if (this.startField_?.sourceBlock_?.isInFlyout) {
            // swallow the click: no editor, no value toggle, nothing - but
            // still bring the block to front like a normal field click would,
            // for visual consistency (harmless no-op outside a flyout anyway).
            this.bringBlockToFront_();
            return;
        }
        originalDoFieldClick.call(this, e);
    };

    // Blockly.Field.prototype.updateEditable (called from Field.prototype.init
    // and again on Block.prototype.setEditable) sets an inline
    // fieldGroup_.style.cursor = this.CURSOR (e.g. "pointer" for dropdowns,
    // "text" for text inputs) whenever the source block "isEditable()" -
    // which is true for flyout blocks too, since flyout blocks aren't
    // read-only. That inline style wins over the block-level
    // ".blocklyDraggable { cursor: grab }" CSS rule, so hovering different
    // parts of a flyout block still shows per-field cursors, contradicting
    // the fact the field is now inert (see doFieldClick_ patch above).
    //
    // Blockly.FieldVariableGetter.prototype.updateEditable already carves out
    // this exact case for its own field type:
    //   !this.sourceBlock_.isInFlyout && this.sourceBlock_.isEditable() && ...
    // Apply the same carve-out at the Field base class so every field type
    // (dropdown, checkbox, colour, date, text input, etc.) gets it, without
    // having to patch each field subtype's CURSOR handling individually.
    const originalUpdateEditable = Blockly.Field.prototype.updateEditable;
    Blockly.Field.prototype.updateEditable = function () {
        originalUpdateEditable.call(this);
        if (this.sourceBlock_?.isInFlyout && this.fieldGroup_) {
            // clear the inline cursor override so the block's own
            // ".blocklyDraggable" grab cursor shows through instead.
            this.fieldGroup_.style.cursor = '';
        }
    };
};

export default fieldFlyoutInteraction;

